/** Formatos de fecha, hora y duración en es-CL (portado del renderer legado). */
import type { RecordingStatus, RecordingWarning } from '@eco/shared'

/** ms → "mm:ss" o "h:mm:ss". */
export function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** ms → "1 h 5 min" / "12 min" / "40 s" (para listas). */
export function formatLength(ms: number): string {
  const min = Math.round(ms / 60_000)
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`
  const h = Math.floor(min / 60)
  const m = min % 60
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`
}

export function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function formatDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** ISO → "Hoy" / "Ayer" / "lun 23 jun" (agrupa la biblioteca). */
export function formatDayGroup(iso: string, now = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((startOf(now) - startOf(d)) / 86_400_000)
  if (diff === 0) return 'Hoy'
  if (diff === 1) return 'Ayer'
  return d.toLocaleDateString('es-CL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
  })
}

export function formatClock(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
}

export const ESTADO_LABEL: Record<RecordingStatus, string> = {
  recording: 'Grabando',
  captured: 'Subiendo',
  queued: 'En cola',
  processing: 'Transcribiendo',
  merging: 'Ordenando',
  summarizing: 'Escribiendo notas',
  completed: 'Lista',
  failed: 'Error',
  retenida: 'Retenida',
  held: 'En espera',
  aborted: 'Cancelada'
}

export type EstadoKind = 'live' | 'work' | 'done' | 'error' | 'hold'

export const ESTADO_KIND: Record<RecordingStatus, EstadoKind> = {
  recording: 'live',
  captured: 'work',
  queued: 'work',
  processing: 'work',
  merging: 'work',
  summarizing: 'work',
  completed: 'done',
  failed: 'error',
  retenida: 'hold',
  held: 'hold',
  aborted: 'error'
}

export const ADVERTENCIA_LABEL: Record<RecordingWarning, string> = {
  trozos_perdidos: 'Faltan fragmentos de audio: puede haber huecos en la transcripción.',
  others_vacia: 'No se escuchó a los demás participantes (¿la pestaña estaba en silencio?).',
  mic_vacia: 'No se escuchó tu micrófono.',
  bleed_alto: 'Tu micrófono captó mucho del audio de la reunión; revisa los hablantes.',
  offscreen_crash: 'La grabación se interrumpió y se recuperó.',
  mic_cambiado: 'El micrófono cambió durante la grabación.',
  finalizada_automaticamente: 'La grabación se cerró sola porque el navegador dejó de responder.',
  pista_fallida: 'Una de las pistas no se pudo transcribir.',
  truncada_por_cuota: 'Se procesó solo una parte por falta de horas.'
}

/**
 * Fecha de una tarea: «2026-09-27» → «sáb 27 sept» (en la hora local, sin corrimiento
 * por zona horaria). Si el modelo dejó texto libre («el jueves»), se muestra tal cual.
 */
export function formatDueDate(fecha: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha)
  if (!m) return fecha
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return d
    .toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(/,/g, '')
    .replace(/\./g, '')
}
